import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

// Donor receipt, ported from the Laravel site's order-details page (app.fundraisingu.net/order/{id}/detail).
const money = (value) => (value || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
// Bootstrap 5 (loaded app-wide) draws a heavy rule under table headers that the Laravel page didn't have.
const noHeaderRule = { borderTop: 0 };
// The corporate theme's dark slate (bg-primary-dark, inverse header) and link color. App.js loads
// codebase.css after the theme, which turns them gray and navy, so they're set here to match Laravel.
const slate = { backgroundColor: '#323d4c' };
const themeLink = { color: '#2facb2' };
// Laravel's solid light divider; Bootstrap 5's <hr> is a 25%-opacity line.
const footerRule = { opacity: 1, height: 0, backgroundColor: 'transparent', borderTop: '1px solid #f2f5f5' };
// Laravel's pages loaded Nunito Sans; the Orders app doesn't, so this page adds it.
const NUNITO_SANS = 'https://fonts.googleapis.com/css?family=Nunito+Sans:300,400,400i,600,700';

export default function Receipt() {
    const { orderId, signature } = useParams();
    const [order, setOrder] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (document.querySelector(`link[href="${NUNITO_SANS}"]`)) return;
        const font = document.createElement('link');
        font.rel = 'stylesheet';
        font.href = NUNITO_SANS;
        document.head.appendChild(font);
    }, []);

    useEffect(() => {
        axios.get(`/api/Receipts/Details/${orderId}/${signature}`)
            .then(res => setOrder(res.data))
            .catch(() => setNotFound(true));
    }, [orderId, signature]);

    // Same page frame as the Laravel donor layout (resources/views/layouts/donor.blade.php).
    const frame = (content) => (
        <div id="page-container" className="page-header-fixed page-header-inverse main-content-boxed">
            <header id="page-header" style={slate}>
                <div className="content-header align-items-center my-4">
                    <div className="content-header-section d-flex align-items-center justify-content-between flex-grow-1">
                        <div style={{ height: '80px', display: 'block', marginRight: '2em' }}>
                            <img src="/media/logo/logo-classic.png" alt="Fundraising U" style={{ height: '100%', top: 0 }} />
                        </div>
                    </div>
                </div>
            </header>
            <main id="main-container" className="mt-4" style={slate}>
                <div className="col-12 col-lg-6 mt-4 mr-lg-auto ml-lg-auto pt-2" style={slate}>
                    <div className="bg-white" style={{ borderRadius: '1.5rem 1.5rem 1.5rem 1.5rem' }}>
                        {content}
                    </div>
                </div>
            </main>
            <footer id="page-footer">
                <div className="content py-20 text-center font-size-sm clearfix d-lg-block d-sm-flex flex-sm-column">
                    <div className="float-none float-lg-left">
                        Fundraising U &copy; {new Date().getFullYear()}
                    </div>
                </div>
            </footer>
        </div>
    );

    if (notFound) {
        return frame(<div className="p-4">We couldn't find this receipt. Please contact <a href="mailto:support@fundraisingu.net">support@fundraisingu.net</a>.</div>);
    }
    if (!order) {
        return frame(<div className="p-4">Loading...</div>);
    }

    return frame(
        <div>
            <div className="p-4">
                <div className="font-size-lg font-w600">
                    Order Detail #{order.id}
                </div>
                <div className="font-size-md font-w600">
                    Thank you for supporting {order.studentSchoolName} - {order.studentTeamName} - {order.studentFirstName} {order.studentLastName} with your purchase.
                    Please keep in mind that the charge will show up on your bank statement as "Fundraising University".
                </div>
                <div className="p-3 ml-auto mr-auto">
                    <div className="block-content p-0">
                        <div className="table table-hover table-responsive">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Item / Card</th>
                                        <th>Option</th>
                                        <th className="text-right">Price</th>
                                    </tr>
                                </thead>
                                <tbody style={noHeaderRule}>
                                    {order.items.map(item => (
                                        <tr key={item.id}>
                                            <td>{item.name}</td>
                                            <td>{item.optionName}</td>
                                            <td className="text-right">{money(item.price)}</td>
                                        </tr>
                                    ))}
                                    {order.discountCards.map(card => (
                                        <tr key={`dc-${card.id}`}>
                                            <td>{card.name}</td>
                                            <td>Digital Discount Card</td>
                                            <td className="text-right">{money(card.price)}</td>
                                        </tr>
                                    ))}
                                    {order.items.length === 0 && (
                                        <tr>
                                            <td>Donation</td>
                                            <td></td>
                                            <td className="text-right">{money(order.orderTotal)}</td>
                                        </tr>
                                    )}
                                    {order.totalShippingCost > 0 && (
                                        <tr>
                                            <td></td>
                                            <td>Shipping Cost</td>
                                            <td className="text-right">{money(order.totalShippingCost)}</td>
                                        </tr>
                                    )}
                                    {order.convenienceFee > 0 && (
                                        <tr>
                                            <td></td>
                                            <td>Convenience Fee - 5%</td>
                                            <td className="text-right">{money(order.convenienceFee)}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="d-flex align-items-center float-right mb-2">
                            <span className="font-size-lg font-w700">
                                Order Total:
                                <span className="ml-1 text-muted font-w600 font-size-sm">
                                    {money(order.orderTotal + order.convenienceFee + order.totalShippingCost)}
                                </span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            {order.showOffers && (
                <div className="p-3 ml-auto mr-auto" style={{ backgroundColor: '#112D48' }}>
                    <div className="row">
                        <div className="col-12">
                            <h6 style={{ color: '#FFFFFF' }}>Click here to access your National Discount Offers: </h6>
                            <a href="https://fundraisingu.enjoymydeals.com/" target="_blank" rel="noreferrer" style={{ ...themeLink, fontWeight: 'bolder' }}>https://fundraisingu.enjoymydeals.com/ (Code: {order.accessCode})</a>
                        </div>
                    </div>
                </div>
            )}
            <div className="p-3 ml-auto mr-auto" style={{ backgroundColor: '#c6c6c6' }}>
                <div className="row">
                    <div className="col-12">
                        <h4>Fundraising University Career Opportunities</h4>
                        <h5>JOIN OUR TEAM AND MAKE A DIFFERENCE IN HIGH SCHOOL SPORTS &amp; ACTIVITIES</h5>
                        <p>
                            Fundraising University is expanding its team and we are actively seeking talented individuals to join us as a Fundraising Coach across the nation. As our company continues to grow, we recognize the importance of expanding our team to better serve our communities and reach new markets. We are looking for driven, dynamic individuals who are passionate about their community and eager to contribute to our success.
                        </p>
                        <div className="mb-3">
                            <strong>Seeking individuals that fulfill these requirements:</strong>
                            <ul>
                                <li>Proven track record in sales a plus, with a focus on achieving targets and driving results.</li>
                                <li>Excellent communication and interpersonal skills, with the ability to engage with Coaches at all levels.</li>
                                <li>Self-motivated and proactive, with a strong drive to succeed in a fast-paced environment.</li>
                                <li>School sports/coaching background also an advantage!</li>
                            </ul>
                        </div>
                        <p>
                            If you are passionate about sports and are eager to take on a new challenge, we would love to hear from you. Join us in shaping the future of the fundraising industry and become a part of our dynamic team.
                        </p>
                        <p>
                            To apply, please submit your resume below. Feel free to reach out if you have any questions or require further information.
                        </p>
                        <p style={{ textAlign: 'center' }}>
                            <a href="mailto:mbahun@fundraisingu.net?subject=I'm%20interested%20in%20a%20position%20with%20Fundraising%20U!" className="btn btn-primary" style={{ fontWeight: 'bold', color: 'white', backgroundColor: '#f7931e' }}>Get Connected Here</a>
                        </p>
                    </div>
                </div>
            </div>
            <div className="p-3 ml-auto mr-auto" style={{ backgroundColor: '#112D48' }}>
                <div className="row">
                    <div className="col-12">
                        <p style={{ color: '#FFFFFF' }}>
                            Do you have a group looking to raise funds NOW? FUNDUNOW! is the FASTEST Fundraiser in the
                            country! If so, please sign up today <a href="https://fundraisingu.net/" target="_blank" rel="noreferrer" style={themeLink}>https://fundraisingu.net/</a>
                        </p>
                        <hr style={footerRule} />
                        <p style={{ color: '#FFFFFF' }}>
                            If you have any questions or concerns regarding this transaction please contact Fundraising
                            University at <a href="mailto:support@fundraisingu.net" style={themeLink}>support@fundraisingu.net</a>
                        </p>
                        <hr style={footerRule} />
                        <p style={{ color: '#FFFFFF' }}>
                            You are receiving this email because you made a donation supporting a fundraising effort in association with {order.fundraiserSchoolName} - {order.fundraiserTeamName}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
